import { CartStore } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import { OlsConfigStore } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import { ProductDetailStore } from '@wsb/guac-widget-shared/lib/common/ols-core/shop-bundle';
import CookieBannerUtils from './ols-core/CookieBannerUtils';

const injectTracker = (trackingId, developerId) => {
  const elementId = `ga-tracker-${trackingId}`;
  // Don't inject the tracker if it already exists on the page
  if (document.getElementById(elementId)) return;

  if (!window.gtag) {
    // We have to insert the global site tag and create the gtag function
    // https://developers.google.com/analytics/devguides/collection/gtagjs/#install_the_global_site_tag
    const gtagScript = document.createElement('script');
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    gtagScript.async = true;
    document.body.appendChild(gtagScript);

    const gtagJs = document.createElement('script');
    gtagJs.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){window.dataLayer.push(arguments);}
      gtag('js', new Date());
    `;
    document.body.appendChild(gtagJs);
  }

  // Finally configure tracker
  const script = document.createElement('script');
  script.id = elementId;
  script.innerHTML = `
    gtag('config', '${trackingId}');
    gtag('set', 'developer_id.${developerId}', true);
  `;
  document.body.appendChild(script);
};

const trackGaEvent = ({ trackingId, developerId }, eventName, data) => {
  injectTracker(trackingId, developerId);
  return window?.gtag('event', eventName, { send_to: trackingId, ...data });
};

export const getNumericPrice = price => {
  if (typeof price === 'object') {
    return price.numeric;
  }
  return +price;
};

export const productMap = ({
  product,
  variantId = null,
  quantity = 1,
  eventName = 'view_item'
}) => {
  let { sku } = product;
  let price = product.sale_price || product.price;

  // Grab SKU and price from variant (if applicable)
  if (variantId && product && product.variants.length) {
    const variant = product.variants.find(v => v.id === variantId);
    sku = (variant && variant.sku) || sku;
    price = (variant && (variant.sale_price || variant.price)) || price;
  }

  const numericPrice = getNumericPrice(price);

  const reportingObj = {
    ecomm_pagetype: eventName === ('add_to_cart' || 'page_view') ? 'cart' : 'product',
    value: numericPrice * quantity,
    items: [
      {
        google_business_vertical: 'retail',
        id: sku,
        name: product.name,
        quantity: quantity,
        price: numericPrice,
        currency: (price && price.currency) || product.currency
      }
    ]
  };

  return reportingObj;
};

export const cartMap = (cart, eventName = 'page_view') => {
  const lineItems = cart?.line_items || [];
  if (!lineItems) return;

  const items = lineItems.map(item => {
    const price = item.variant?.sale_price || item.variant?.price || item.sale_price || item.price;
    const numericPrice = getNumericPrice(price);

    return {
      google_business_vertical: 'retail',
      id: item.variant?.sku || item.sku,
      name: item.variant?.name || item.name,
      quantity: item.quantity,
      price: numericPrice
    };
  });

  const data = {
    ecomm_pagetype: eventName === 'purchase' ? 'purchase' : 'cart ',
    value: getNumericPrice(cart?.total || 0),
    currency: cart?.currency,
    total_quantity: cart?.total_quantity,
    items
  };

  return data;
};

class GoogleAnalytics {
  logEvent(eventName, data = {}) {
    if (window.gtag && window._commercegaID) {
      return window.gtag('event', eventName, { send_to: window._commercegaID, ...data });
    }
  }

  // TODO: See google docs for all event data needed for various events:
  // https://developers.google.com/analytics/devguides/collection/gtagjs/enhanced-ecommerce

  logViewItem() {
    const { product } = ProductDetailStore.getState();
    this.logServerEvent('view_item', [product.sku], productMap({ product }));
  }

  logAddToCart(variantId = null, quantity = 1) {
    const { product } = ProductDetailStore.getState();
    const eventName = 'add_to_cart';
    this.logServerEvent(
      eventName,
      [product.sku],
      productMap({ product, variantId, quantity, eventName })
    );
  }

  logViewCart() {
    const { cart } = CartStore.getState();
    this.logServerEvent(
      'page_view',
      cart?.line_items?.map(item => item.variant?.sku || item.sku) || [],
      cartMap(cart)
    );
  }

  logBeginCheckout() {
    const { cart } = CartStore.getState();
    this.logServerEvent(
      'begin_checkout',
      cart?.line_items?.map(item => item.variant?.sku || item.sku) || [],
      cartMap(cart)
    );
  }

  logServerEvent(eventName, skus = [], data = {}) {
    const adTrackingData = OlsConfigStore.getState().ga_ad_tracking || {};
    const {
      tracking_id: trackingId,
      product_id_api_url: apiUrl,
      developer_id: developerId
    } = adTrackingData;

    const websiteId = OlsConfigStore.getWebsiteId();

    if (!apiUrl || !CookieBannerUtils.isAccepted || !websiteId) return;

    try {
      const requestData = {
        website_id: websiteId,
        skus
      };

      window
        ?.fetch(apiUrl, {
          method: 'POST',
          body: JSON.stringify(requestData),
          headers: {
            'Content-type': 'application/json'
          }
        })
        .then(res => res.json())
        .then(res => {
          // We need swap SKUs with the google product ids we get back:
          const { googleProductIds = {} } = res;
          const trackingData = { ...data };
          trackingData.items = trackingData?.items?.map(item => ({
            ...item,
            id: googleProductIds[item.id] || item.id
          }));
          trackGaEvent({ trackingId, developerId }, eventName, trackingData);
        })
        .catch(() => trackGaEvent({ trackingId, developerId }, eventName, data));
    } catch (ex) {
      return;
    }
  }
}

export default new GoogleAnalytics();
